import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { apiUrl, fetchJson } from '../../apiBase';

const api = {
  classes: (school) => apiUrl(`/admin/classes/${encodeURIComponent(school)}`),
  studentsAll: (school) => apiUrl(`/admin/students/${encodeURIComponent(school)}/all`),
};

function toText(value) {
  return String(value ?? '').trim();
}

function normalizeStudent(item) {
  const id = toText(item?.id ?? item?.student_id);
  const name = toText(item?.name ?? item?.student_name);
  const surname = toText(item?.surname ?? item?.student_surname);
  const className = toText(item?.class_name ?? item?.class ?? item?.className);
  const studentNo = toText(item?.student_no ?? item?.number ?? item?.studentNumber);
  const fullName = `${name} ${surname}`.trim() || id;

  return {
    id,
    name,
    surname,
    fullName,
    className,
    studentNo,
  };
}

function sortStudents(items) {
  return [...items].sort((a, b) => {
    const byClass = a.className.localeCompare(b.className, 'tr', { sensitivity: 'base' });
    if (byClass !== 0) return byClass;

    const bySurname = a.surname.localeCompare(b.surname, 'tr', { sensitivity: 'base' });
    if (bySurname !== 0) return bySurname;

    const byName = a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
    if (byName !== 0) return byName;

    return a.id.localeCompare(b.id, 'tr', { sensitivity: 'base' });
  });
}

export default function Student360DirectoryPage({ schoolId, onError, user }) {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [classFilter, setClassFilter] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTeacher = user?.role === 'teacher';

  const authHeaders = useMemo(() => {
    const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(async () => {
    if (!schoolId) return;

    setLoading(true);
    try {
      const [studentsRes, classesRes] = await Promise.all([
        fetchJson(api.studentsAll(schoolId), { headers: authHeaders }),
        fetchJson(api.classes(schoolId), { headers: authHeaders }),
      ]);

      if (!studentsRes.ok) {
        throw new Error('students');
      }

      const studentBody = await studentsRes.json();
      const classBody = classesRes.ok ? await classesRes.json() : [];

      const studentItems = Array.isArray(studentBody) ? studentBody : studentBody?.students || [];
      const classItems = Array.isArray(classBody) ? classBody : classBody?.classes || [];

      setStudents(
        sortStudents(
          studentItems
            .map(normalizeStudent)
            .filter((item) => item.id)
        )
      );

      setClasses(
        classItems
          .map((item) => toText(typeof item === 'string' ? item : item?.name))
          .filter(Boolean)
      );
    } catch (_) {
      setStudents([]);
      setClasses([]);
      onError?.('Öğrenci360 listesi getirilemedi');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, onError, schoolId]);

  useEffect(() => {
    load();
  }, [load]);

  const classOptions = useMemo(() => {
    const values = new Set(classes);
    students.forEach((item) => {
      if (item.className) values.add(item.className);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, 'tr', { sensitivity: 'base' }));
  }, [classes, students]);

  const filteredStudents = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');

    return students.filter((item) => {
      if (classFilter && item.className !== classFilter) {
        return false;
      }

      if (!q) {
        return true;
      }

      const haystack = [
        item.fullName,
        item.className,
        item.studentNo,
        item.id,
      ]
        .join(' ')
        .toLocaleLowerCase('tr');

      return haystack.includes(q);
    });
  }, [classFilter, query, students]);

  const openStudent360 = (studentId) => {
    window.open(`/admin/student360?student_id=${encodeURIComponent(studentId)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box>
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              label="Öğrenci ara"
              placeholder="Ad, soyad, numara veya şube"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Şube"
              value={classFilter}
              onChange={(event) => setClassFilter(event.target.value)}
              fullWidth
            >
              <MenuItem value="">Tüm şubeler</MenuItem>
              {classOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap' }}>
              <Chip color="primary" label={`${filteredStudents.length} öğrenci`} />
              <Chip variant="outlined" label={isTeacher ? 'Öğretmen görünümü' : 'Yönetici görünümü'} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={isMobile ? 108 : 52} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : filteredStudents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Öğrenci bulunamadı
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Arama veya şube filtresini değiştirerek tekrar deneyin.
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box>
            {filteredStudents.map((item, index) => (
              <Box
                key={item.id}
                sx={{
                  p: 2,
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  bgcolor: index % 2 === 0 ? 'background.default' : 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {item.fullName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.className || 'Şube yok'}
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => openStudent360(item.id)}>
                    öğrenci360
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={`No: ${item.studentNo || '-'}`} />
                  <Chip size="small" label={`ID: ${item.id}`} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Öğrenci</TableCell>
                  <TableCell>Şube</TableCell>
                  <TableCell>Numara</TableCell>
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{item.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{item.className || '-'}</TableCell>
                    <TableCell>{item.studentNo || '-'}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => openStudent360(item.id)}>
                        öğrenci360
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
